import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Wallet, Clock, Download, FileText, ArrowDownCircle, ArrowUpCircle, Landmark, ShieldCheck } from "lucide-react";

const KIND_LABELS = {
  order_net: "Netto ordine", delivery_pay: "Compenso consegna", bonus: "Bonus",
  incentive: "Incentivo", penalty: "Penalità", payout: "Liquidazione", adjustment: "Rettifica",
  refund: "Rimborso",
};
const STATUS_LABELS = {
  pending: "In attesa", available: "Disponibile", in_payout: "In liquidazione",
  paid_out: "Liquidato", reversed: "Stornato",
};
const STATUS_COLORS = {
  pending: "text-amber-300 bg-amber-400/10 border-amber-400/30",
  available: "text-[#00E676] bg-[#00E676]/10 border-[#00E676]/30",
  in_payout: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30",
  paid_out: "text-zinc-300 bg-zinc-500/10 border-zinc-500/30",
  reversed: "text-red-300 bg-red-500/10 border-red-500/30",
};
const PAYOUT_LABELS = {
  in_attesa: "In attesa", approvato: "Approvato", in_elaborazione: "In elaborazione",
  pagato: "Pagato", annullato: "Annullato",
};

// Metodo di pagamento (IBAN) — riutilizzabile per rider / attività / professionista.
function PayoutMethodCard() {
  const [pm, setPm] = useState(null);
  const [edit, setEdit] = useState(false);
  const [iban, setIban] = useState("");
  const [holder, setHolder] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/wallet/me/payout-method").then(({ data }) => { setPm(data); if (data.holder) setHolder(data.holder); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/wallet/me/payout-method", { iban, holder });
      toast.success("IBAN salvato"); setEdit(false); setIban(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "IBAN non valido"); }
    setSaving(false);
  };
  return (
    <div className="rounded-2xl p-5 border border-white/10 bg-[#1C1C1F]" data-testid="payout-method-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400"><Landmark className="h-4 w-4 text-[#00E676]" /> Metodo di pagamento (IBAN)</div>
        {pm?.configured && !edit && <button data-testid="payout-method-edit" onClick={() => setEdit(true)} className="text-xs text-[#00E676]">Modifica</button>}
      </div>
      {pm?.configured && !edit ? (
        <div>
          <div className="font-mono text-lg tracking-wider" data-testid="payout-iban-display">{pm.iban_masked}</div>
          <div className="text-sm text-zinc-400 mt-1">Intestatario: {pm.holder}</div>
          <div className="text-[11px] text-zinc-500 mt-2 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-[#00E676]" /> Le liquidazioni verranno accreditate su questo IBAN via bonifico.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {!pm?.configured && <div className="text-sm text-amber-300/90 bg-amber-400/10 border border-amber-400/25 rounded-lg p-2.5" data-testid="payout-method-missing">Aggiungi il tuo IBAN per poter ricevere i pagamenti delle tue liquidazioni.</div>}
          <div><label className="text-xs text-zinc-400">IBAN</label><Input data-testid="payout-iban-input" value={iban} onChange={(e) => setIban(e.target.value.toUpperCase())} placeholder="IT60 X054 2811 1010 0000 0123 456" className="bg-[#0A0A0C] border-white/10 mt-1 font-mono" /></div>
          <div><label className="text-xs text-zinc-400">Intestatario del conto</label><Input data-testid="payout-holder-input" value={holder} onChange={(e) => setHolder(e.target.value)} placeholder="Mario Rossi" className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
          <div className="flex gap-2">
            <Button size="sm" data-testid="payout-method-save" disabled={saving} onClick={save} className="bg-[#00E676] text-black rounded-md">{saving ? "Salvataggio…" : "Salva IBAN"}</Button>
            {pm?.configured && <Button size="sm" variant="secondary" onClick={() => { setEdit(false); setIban(""); }}>Annulla</Button>}
          </div>
        </div>
      )}
    </div>
  );
}

// Estratto conto riutilizzabile per rider / attività / professionista.
export default function WalletStatement() {
  const [w, setW] = useState(null);
  const [txns, setTxns] = useState([]);
  const [filter, setFilter] = useState("");

  const load = () => {
    api.get("/wallet/me").then(({ data }) => setW(data)).catch(() => {});
    api.get("/wallet/me/transactions").then(({ data }) => setTxns(data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const download = async (fmt) => {
    try {
      const res = await api.get(`/wallet/me/export.${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `estratto_conto.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { /* ignore */ }
  };

  if (!w) return <div className="text-zinc-500 py-16 text-center">Caricamento estratto conto…</div>;
  const rows = filter ? txns.filter((t) => t.status === filter) : txns;

  return (
    <div className="space-y-6" data-testid="wallet-statement">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div data-testid="wallet-available" className="rounded-2xl p-5 border border-[#00E676]/30 bg-gradient-to-br from-[#00E676]/10 to-[#131316]">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400"><Wallet className="h-4 w-4 text-[#00E676]" /> Saldo disponibile</div>
          <div className="text-3xl font-display font-bold text-[#00E676] mt-2">€{Number(w.available).toFixed(2)}</div>
        </div>
        <div data-testid="wallet-pending" className="rounded-2xl p-5 border border-amber-400/25 bg-[#1C1C1F]">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400"><Clock className="h-4 w-4 text-amber-300" /> In attesa (clearing)</div>
          <div className="text-3xl font-display font-bold text-amber-300 mt-2">€{Number(w.pending).toFixed(2)}</div>
        </div>
        <div className="rounded-2xl p-5 border border-white/10 bg-[#1C1C1F] flex flex-col justify-between">
          <div className="text-xs uppercase tracking-widest text-zinc-400">Esporta report</div>
          <div className="flex gap-2 mt-2">
            <Button size="sm" variant="secondary" data-testid="wallet-export-csv" onClick={() => download("csv")}><Download className="h-4 w-4 mr-1" /> CSV</Button>
            <Button size="sm" variant="secondary" data-testid="wallet-export-pdf" onClick={() => download("pdf")}><FileText className="h-4 w-4 mr-1" /> PDF</Button>
          </div>
        </div>
      </div>

      <PayoutMethodCard />

      {w.payouts && w.payouts.length > 0 && (
        <div>
          <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Storico liquidazioni</h3>
          <div className="space-y-2">
            {w.payouts.map((p) => (
              <div key={p.id} data-testid={`my-payout-${p.id}`} className="flex items-center justify-between bg-[#1C1C1F] border border-white/10 rounded-lg p-3">
                <div className="text-sm"><b>€{Number(p.amount).toFixed(2)}</b> <span className="text-zinc-500">· {String(p.created_at).slice(0, 10)}</span></div>
                <span className="text-[11px] rounded-full px-2 py-0.5 border border-white/15 text-zinc-300">{PAYOUT_LABELS[p.status] || p.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs uppercase tracking-widest text-zinc-500">Storico movimenti</h3>
          <div className="flex gap-1">
            {["", "available", "pending", "paid_out"].map((s) => (
              <button key={s} data-testid={`wallet-filter-${s || "all"}`} onClick={() => setFilter(s)} className={`text-[11px] px-2.5 py-1 rounded-full border ${filter === s ? "border-[#00E676] text-[#00E676]" : "border-white/10 text-zinc-400"}`}>{s ? STATUS_LABELS[s] : "Tutti"}</button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          {rows.map((t) => (
            <div key={t.id} data-testid={`wallet-txn-${t.id}`} className="flex items-center justify-between bg-[#1C1C1F] border border-white/10 rounded-lg p-3">
              <div className="flex items-center gap-3 min-w-0">
                {t.amount >= 0 ? <ArrowDownCircle className="h-5 w-5 text-[#00E676] shrink-0" /> : <ArrowUpCircle className="h-5 w-5 text-red-400 shrink-0" />}
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{KIND_LABELS[t.kind] || t.kind}</div>
                  <div className="text-xs text-zinc-500 truncate">{t.description} · {String(t.created_at).slice(0, 16).replace("T", " ")}</div>
                </div>
              </div>
              <div className="text-right shrink-0 ml-3">
                <div className={`font-semibold ${t.amount >= 0 ? "text-[#00E676]" : "text-red-400"}`}>{t.amount >= 0 ? "+" : ""}€{Number(t.amount).toFixed(2)}</div>
                <span className={`text-[10px] rounded-full px-1.5 py-0.5 border ${STATUS_COLORS[t.status] || ""}`}>{STATUS_LABELS[t.status] || t.status}</span>
              </div>
            </div>
          ))}
          {rows.length === 0 && <div className="text-zinc-500 py-12 text-center border border-dashed border-white/10 rounded-xl">Nessun movimento.</div>}
        </div>
      </div>
    </div>
  );
}
